import {
  For,
  Show,
  createEffect,
  createMemo,
  createSignal,
  onMount,
} from "solid-js";

import AffiliateClaiTip from "@/components/Base/AffiliateClaiTip";
import Icon, { svgPathTypes } from "@/components/Icon";
import { useGame } from "@/hooks/game/useGame.ts";
import { useRouter } from "@/hooks/useRouter.tsx";
import { t } from "@/i18n";
import { isMobile } from "@/lib/isMobile";
import { getFileUrl } from "@/lib/resFolder";
import { sdkManager } from "@/lib/sdk/sdkManager";
import { removeLocalePrefix } from "@/lib/urlUtils.ts";
import { sysConfigStore } from "@/stores/sysConfigStore";
import { userStore } from "@/stores/userStore";
import { useLocation } from "@solidjs/router";

export default function BottomNav() {
  const [i18nReady, setI18nReady] = createSignal(false);
  onMount(() => {
    (async () => {
      setI18nReady(true);
    })();
  });
  const defaultSportGameId = createMemo(() => {
    if (!sysConfigStore.initReady) {
      return 0;
    }
    return sysConfigStore.sport_config.sport_game_id;
  });

  const navItems = createMemo(() => {
    return [
      {
        label: t("home.menu"),
        icon: "general/tabbar_menu",
        activeIcon: "general/tabbar_menu_active",
        path: "/menu",
        report: "menu_nav_click",
      },
      {
        label: t("home.title"),
        icon: "general/tabbar_home",
        activeIcon: "general/tabbar_home_active",
        path: "/",
        report: "home_nav_click",
      },
      {
        label: t("home.casino"),
        icon: "general/tabbar_casino",
        activeIcon: "general/tabbar_casino_active",
        path: "/casino",
        report: "casino_nav_click",
      },
      {
        label: t("home.sports"),
        icon: "general/tabbar_sports",
        activeIcon: "general/tabbar_sports_active",
        path: "/sports",
        report: "sports_nav_click",
      },
      {
        label: t("home.affiliate"),
        icon: "general/tabbar_affiliate",
        activeIcon: "general/tabbar_affiliate_active",
        path: "/affiliate",
        showRedDot: userStore.affiliateRewardTip,
        report: "affiliate_nav_click",
      },
      // {
      //   label: t("home.account"),
      //   icon: "general/bottom_user",
      //   path: "profile",
      // },
    ];
  });

  const { push, replace } = useRouter();
  console.log(`BottomNav location.pathname`);
  if (!isMobile()) replace("/");

  const { enterGame } = useGame();
  const location = useLocation();

  // 使用 createMemo 创建响应式的 currentPath
  const currentPath = createMemo(() => {
    return removeLocalePrefix(location.pathname) || "";
  });

  const [activeItem, setActiveItem] = createSignal(currentPath());

  // 监听 location.pathname 的变化，更新 activeItem
  createEffect(() => {
    const path = currentPath();
    setActiveItem(path);
  });

  const isActive = createMemo(
    () =>
      (path: string | ((path: string) => boolean)): boolean => {
        console.log("path", path, activeItem());
        if (typeof path === "function") {
          return path(activeItem());
        }
        const current = activeItem();

        // 首页作为兜底路由，匹配所有非其他 tabbar 路由的路径
        if (path === "/") {
          // 使用正则表达式匹配其他 tabbar 路由
          const tabbarRoutesRegex =
            /^\/(menu|casino|sports|affiliate)(\/|\?|$)/;
          return current === "/" || !tabbarRoutesRegex.test(current);
        }

        // 使用正则表达式匹配路径：支持路径参数和查询参数
        // 例如：/affiliate 匹配 /affiliate、/affiliate/23、/affiliate?a=、/affiliate/23?b=1 等
        const pathRegex = new RegExp(
          `^${path.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(/|\\?|$)`,
        );
        return pathRegex.test(current);
      },
  );

  return (
    <Show when={i18nReady()} fallback={<></>}>
      <div class="safe-bottom-nav-padding px-[12px]">
        <div
          class="prevent-zoom bg-(--general-tmd70) grid w-full grid-cols-5 items-center justify-between rounded-[100px] bg-cover bg-no-repeat px-[4px] backdrop-blur-[7.5px]"
          style={{
            "background-image": `url(${getFileUrl("tabbar_border.png", "home")})`,
            "box-shadow":
              "-20px 68px 20px 0 rgba(0, 0, 0, 0.00), -13px 43px 18px 0 rgba(0, 0, 0, 0.01), -7px 24px 15px 0 rgba(0, 0, 0, 0.04), -3px 11px 11px 0 rgba(0, 0, 0, 0.07), -1px 3px 6px 0 rgba(0, 0, 0, 0.08)",
          }}
        >
          <For each={navItems()}>
            {(item) => (
              <div
                class="prevent-zoom relative flex cursor-pointer flex-col items-center"
                data-path={item.path}
                data-active={activeItem()}
                classList={{
                  "text-down-yes-r": isActive()(item.path),
                  "text-down-font-not": !isActive()(item.path),
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  sdkManager.tdReportEvent(item.report, {
                    source: window.location.pathname,
                  });
                  if (item.path === "/sports") {
                    enterGame(defaultSportGameId());
                  } else {
                    if (
                      item.path === "/menu" &&
                      location.pathname.includes(item.path)
                    ) {
                      setActiveItem("");
                      push("/");
                      return;
                    } else {
                      push(item.path);
                    }
                  }
                  setActiveItem(item.path);
                }}
              >
                <Show when={item.showRedDot} fallback={<></>}>
                  <AffiliateClaiTip class="right-[-19px] top-[3px]" />
                </Show>
                <Icon
                  name={
                    isActive()(item.path)
                      ? (item.activeIcon as svgPathTypes)
                      : (item.icon as svgPathTypes)
                  }
                  class={`aspect-[68.6/60] h-[60px] ${isActive()(item.path) ? "text-down-yes-r" : "text-down-font-not"}`}
                />
                <div
                  class="absolute bottom-[7px] left-0 w-full text-center text-[12px] font-[400] leading-[15px]"
                  classList={{ "font-[700]!": isActive()(item.path) }}
                >
                  {item.label}
                </div>
              </div>
            )}
          </For>
        </div>
      </div>
    </Show>
  );
}
